/* eslint-disable react-refresh/only-export-components */
'use client'

import React, { createContext, useContext, useMemo, useState } from 'react'

export type DocumentPreviewSource = {
  label: string
  url: string
  mimeType: string
}

type DocumentPreviewContextType = {
  // Derived: the currently-selected source's url + mimeType, or null when nothing is active.
  previewUrl: string | null
  previewMimeType?: string

  // Full list of preview sources (segments) and the active index.
  sources: DocumentPreviewSource[]
  selectedIndex: number
  setSelectedIndex(index: number): void

  // Backwards-compat: single-source preview. Clears existing sources.
  setPreviewUrl(url: string | null, mimeType?: string): void

  // Multi-source preview. Renders a segmented control in the sidebar.
  setPreviewSources(sources: DocumentPreviewSource[], defaultIndex?: number): void
}

const DocumentPreviewContext = createContext<DocumentPreviewContextType | undefined>(undefined)

export const DocumentPreviewProvider = ({ children }: { children: React.ReactNode }) => {
  const [sources, setSources] = useState<DocumentPreviewSource[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const active = sources[selectedIndex]

  const value = useMemo<DocumentPreviewContextType>(
    () => ({
      previewUrl: active?.url ?? null,
      previewMimeType: active?.mimeType,
      sources,
      selectedIndex,
      setSelectedIndex,
      setPreviewUrl: (url, mimeType) => {
        if (!url) {
          setSources([])
          setSelectedIndex(0)
          return
        }
        setSources([{ label: 'Document', url, mimeType: mimeType ?? '' }])
        setSelectedIndex(0)
      },
      setPreviewSources: (next, defaultIndex = 0) => {
        setSources(next)
        setSelectedIndex(Math.min(defaultIndex, Math.max(next.length - 1, 0)))
      },
    }),
    [active, sources, selectedIndex],
  )

  return <DocumentPreviewContext.Provider value={value}>{children}</DocumentPreviewContext.Provider>
}

export function useDocumentPreview() {
  const context = useContext(DocumentPreviewContext)

  if (context === undefined) {
    throw new Error('useDocumentPreview must be used within a DocumentPreviewProvider')
  }

  return context
}
